import React from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: "Quick Links",
      links: [
        { name: "Home", path: "/" },
        { name: "About", path: "/about-us" },
        { name: "Contact", path: "/contact" },
      ],
    },
    {
      title: "Contact",
      links: [
        {
          name: "+91 8573036150",
          icon: "Phone",
          path: "tel:+918573036150",
        },
        {
          name: "contact@kharbandamotors.com",
          icon: "Mail",
          path: "mailto:contact@kharbandamotors.com",
        },
        {
          name: "28, Nawab Yusuf Rd, Civil Lines, Prayagraj, Uttar Pradesh 211001",
          icon: "MapPin",
          path: "https://share.google/MSAT1SFLXtrYXI0Tm",
        },
      ],
    },
  ];

  const socialLinks = [
    { name: "Facebook", icon: "Facebook", url: "#" },
    { name: "Instagram", icon: "Instagram", url: "#" },
    { name: "YouTube", icon: "Youtube", url: "#" },
  ];

  const paymentMethods = [
    { name: "UPI", icon: "Smartphone" },
    { name: "Net Banking", icon: "Building" },
    { name: "E-Wallets", icon: "Wallet" },
  ];

  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="flex justify-center overflow-hidden">
                <img
                  src="/Untitled-removebg-preview (11).webp"
                  alt="Kharbanda Motors Logo"
                  className="h-14 object-contain"
                />
              </div>
            </Link>

            <p className="text-gray-300 mb-6 leading-relaxed w-2/3">
              Since 1984, Kharbanda Motors has been a cornerstone in Prayagraj's
              automotive industry, delivering top-tier services. Specializing in
              genuine spare parts and meticulous repairs, we remain committed to
              quality and customer satisfaction. Trust us with your vehicle for
              an exceptional experience every time.
            </p>

            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors duration-normal"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links + Contact */}
          {footerSections?.map((section) => (
            <div key={section?.title}>
              <h3 className="font-semibold text-white mb-4">
                {section?.title}
              </h3>
              <ul className="space-y-3">
                {section?.links?.map((link) => {
                  const isExternal =
                    link?.path?.startsWith("http") ||
                    link?.path?.startsWith("mailto:") ||
                    link?.path?.startsWith("tel:");

                  const Wrapper = isExternal ? "a" : Link;

                  return (
                    <li key={link?.name}>
                      <Wrapper
                        {...(isExternal
                          ? {
                              href: link?.path,
                              target: "_blank",
                              rel: "noopener noreferrer",
                            }
                          : { to: link?.path })}
                        className="flex items-start space-x-2 text-gray-300 hover:text-white transition-colors duration-normal"
                      >
                        {link?.icon && (
                          <Icon
                            name={link?.icon}
                            size={16}
                            color="var(--color-accent)"
                          />
                        )}
                        <span>{link?.name}</span>
                      </Wrapper>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Trust Badges & Certifications */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Quality Assurance */}
            <div className="text-center lg:text-left">
              <h4 className="font-semibold text-white mb-3">
                Quality Assurance
              </h4>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <div className="flex items-center space-x-2 px-3 py-2 bg-white/10 rounded-lg">
                  <Icon name="Shield" size={16} color="var(--color-success)" />
                  <span className="text-sm">ISO Certified</span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-2 bg-white/10 rounded-lg">
                  <Icon name="Award" size={16} color="var(--color-success)" />
                  <span className="text-sm">OEM Quality</span>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="text-center lg:text-right">
              <h4 className="font-semibold text-white mb-3">Secure Payments</h4>
              <div className="flex justify-center lg:justify-end space-x-6">
                {paymentMethods?.map((method) => (
                  <div
                    key={method?.name}
                    className="flex flex-col items-center space-y-1"
                  >
                    <div
                      className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center"
                      title={method?.name}
                    >
                      <Icon name={method?.icon} size={20} color="white" />
                    </div>
                    <span className="text-xs text-white/80">
                      {method?.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div className="text-gray-400 text-sm text-center lg:text-left">
              © {currentYear} Kharbanda Motors. All rights reserved. |
              <span className="ml-1">Privacy Policy</span> |
              <span className="ml-1">Terms of Service</span> |
              <span className="ml-1">Cookie Policy</span>
            </div>

            <div className="flex items-center space-x-4 text-gray-400 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} />
                <span>Prayagraj</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Globe" size={16} />
                <span>Serving PAN India</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
